import React, { useState, useEffect } from 'react';
import { X, HelpCircle, Loader2 } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FAQModal = ({ isOpen, onClose }) => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchFAQs();
    }
  }, [isOpen]);

  const fetchFAQs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/faqs`);
      setFaqs(response.data.faqs || []);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
      // Fallback to default FAQs if API fails
      setFaqs(getDefaultFAQs());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultFAQs = () => [
    {
      id: '1',
      question: 'What is WillKart?',
      answer: 'WillKart is an AI-powered platform that helps you create legally valid wills in minutes. Our intelligent system guides you through the process, ensuring your assets and wishes are properly documented and protected.'
    },
    {
      id: '2',
      question: 'Is my will legally valid?',
      answer: 'Yes, wills created through WillKart are legally valid in India. We follow all legal requirements under the Indian Succession Act, 1925. However, the will must be properly signed and witnessed as per legal requirements.'
    },
    {
      id: '3',
      question: 'How secure is my data?',
      answer: 'Your data security is our top priority. We use bank-grade 256-bit encryption, secure cloud storage, and strict access controls. Your personal information is never shared with third parties without your consent.'
    },
    {
      id: '4',
      question: 'How long does it take to create a will?',
      answer: 'Most users complete their will in 15-30 minutes. Our AI-powered system streamlines the process by asking relevant questions and auto-filling information where possible.'
    },
    {
      id: '5',
      question: 'Can I update my will after creating it?',
      answer: 'Yes, you can update your will anytime. We recommend reviewing and updating your will whenever there are significant life changes such as marriage, birth of children, or acquisition of new assets.'
    },
    {
      id: '6',
      question: 'What documents do I need to create a will?',
      answer: 'You\'ll need basic identification (Aadhaar/PAN), details of your assets (property documents, bank accounts, investments), and information about your beneficiaries. Our system will guide you through what\'s needed.'
    },
    {
      id: '7',
      question: 'Who can be a witness for my will?',
      answer: 'Any person of sound mind and above 18 years of age can be a witness. However, beneficiaries named in the will or their spouses should not be witnesses to avoid potential legal complications.'
    },
    {
      id: '8',
      question: 'What happens to my will after I create it?',
      answer: 'Your will is securely stored in our system. You can download it anytime, and we recommend keeping physical copies in a safe place. You can also share access with trusted family members or your lawyer.'
    },
    {
      id: '9',
      question: 'Do I need a lawyer to create a will?',
      answer: 'No, you don\'t need a lawyer to create a will with WillKart. Our platform is designed to help you create a legally valid will without legal assistance. However, for complex estates, you may want to consult a lawyer.'
    },
    {
      id: '10',
      question: 'What is the cost of creating a will?',
      answer: 'Our pricing is transparent and affordable. You can view the current pricing after signing up. We offer one-time payment for will creation with optional annual renewal for updates and secure storage.'
    }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[85vh] mx-4 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-slate-900 border-b border-slate-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-amber-400" />
            </div>
            <h2 className="text-xl font-semibold text-white">Frequently Asked Questions</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        
        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(85vh-80px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
            </div>
          ) : (
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={faq.id || index}
                  value={`faq-${index}`}
                  className="border border-slate-700 rounded-lg overflow-hidden bg-slate-800/50 data-[state=open]:bg-slate-800"
                >
                  <AccordionTrigger className="px-4 py-4 text-left text-white hover:text-amber-400 hover:no-underline [&[data-state=open]]:text-amber-400">
                    <span className="pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 text-slate-300 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
        
        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-900 border-t border-slate-700 px-6 py-4">
          <p className="text-sm text-slate-400 text-center">
            Still have questions?{' '}
            <button 
              onClick={onClose}
              className="text-amber-400 hover:text-amber-300 font-medium"
            >
              Contact us
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default FAQModal;
